import React, { memo, useMemo } from 'react';

import { Box, Badge as UCLBadge } from '@rbilabs/universal-components';
import { Image } from 'expo-image';
import { Tabs } from 'expo-router';
import { useIntl } from 'react-intl';
import { InteractionManager, StyleSheet, Text, TouchableOpacity, View } from 'react-native';

import { StandardMobileHeader } from 'components/app-header/mobile-header';
import { useAppTakeover } from 'components/app-takeover/use-app-takeover';
import { useIsDesktopWebBreakpoint } from 'hooks/use-media-query';
import {
  ClickEventComponentNames,
  CustomEventNames,
  EventTypes,
  logRBIEvent,
} from 'state/amplitude';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';
import { primitive } from 'styles/constants/primitives';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';
import { isWeb } from 'utils/environment';

export const unstable_settings = {
  initialRouteName: 'index',
};

function TabsLayout() {
  const { formatMessage } = useIntl();
  const isDesktopWeb = useIsDesktopWebBreakpoint();

  const screenOptions = useMemo(
    () => ({
      freezeOnBlur: true,
      lazy: true,
      header: () => <StandardMobileHeader />,
      tabBarButton: NativeTouchFeedbackTabBarIcon,
      tabBarStyle: tabBarStyles.tabBarStyle,
      tabBarLabelPosition: 'below-icon' as const,
      sceneStyle: { backgroundColor: primitive.bk.$mayoEggWhite },
    }),
    []
  );

  return (
    <Tabs screenOptions={screenOptions}>
      <Tabs.Screen
        name="index"
        {...ANALYTICS_HOME}
        options={useMemo(
          () => ({
            header: () => <StandardMobileHeader hideTsm={!isDesktopWeb} />,
            title: formatMessage({ id: 'navHome' }),
            tabBarButtonTestID: getTestId('Home'),
            tabBarLabel: ({ focused }: { focused: boolean }) => (
              <TabLabel id="navHome" focused={focused} />
            ),
            tabBarIcon: ({ focused }: { focused: boolean }) => (
              <TabBarIcon name="home" focused={focused} altId="navHome" />
            ),
          }),
          [formatMessage, isDesktopWeb]
        )}
      />
      <Tabs.Screen
        name="menu"
        {...ANALYTICS_MENU}
        options={useMemo(
          () => ({
            title: formatMessage({ id: 'navMenu' }),
            tabBarButtonTestID: getTestId('Menu'),
            tabBarLabel: ({ focused }: { focused: boolean }) => (
              <TabLabel id="navMenu" focused={focused} />
            ),
            headerShown: false,
            tabBarIcon: ({ focused }: { focused: boolean }) => (
              <TabBarIcon name="menu" focused={focused} altId="navMenu" />
            ),
          }),
          [formatMessage]
        )}
      />
      <Tabs.Screen
        name="my-code"
        {...ANALYTICS_MY_CODE}
        options={useMemo(
          () => ({
            title: formatMessage({ id: 'navMyCode' }),
            tabBarButtonTestID: getTestId('My-Code'),
            tabBarItemStyle: {
              display: isDesktopWeb ? 'none' : 'flex',
            },
            tabBarLabel: ({ focused }: { focused: boolean }) => (
              <TabLabel id="navMyCode" focused={focused} />
            ),
            tabBarIcon: ({ focused }: { focused: boolean }) => (
              <TabBarIcon
                name="myCode"
                focused={focused}
                BadgeComponent={MyCodeBadge}
                altId="navMyCode"
              />
            ),
          }),
          [formatMessage, isDesktopWeb]
        )}
      />
      <Tabs.Screen
        name="offers"
        {...ANALYTICS_OFFERS}
        options={useMemo(
          () => ({
            title: formatMessage({ id: 'navOffers' }),
            tabBarButtonTestID: getTestId('Offers'),
            tabBarLabel: ({ focused }: { focused: boolean }) => (
              <TabLabel id="navOffers" focused={focused} />
            ),
            tabBarIcon: ({ focused }: { focused: boolean }) => (
              <TabBarIcon name="offers" focused={focused} altId="navOffers" />
            ),
            headerShown: false,
          }),
          [formatMessage]
        )}
      />
      <Tabs.Screen
        name="rewards"
        {...ANALYTICS_REWARDS}
        options={useMemo(
          () => ({
            title: formatMessage({ id: 'navRewards' }),
            tabBarButtonTestID: getTestId('Rewards'),
            tabBarLabel: ({ focused }: { focused: boolean }) => (
              <TabLabel id="navRewards" focused={focused} />
            ),
            tabBarIcon: ({ focused }: { focused: boolean }) => (
              <TabBarIcon name="rewards" focused={focused} altId="navRewards" />
            ),
          }),
          [formatMessage]
        )}
      />
    </Tabs>
  );
}

export default memo(TabsLayout);

const getTestId = (name: string) => `nav-with-picture-icon-${name}`;

const createAnalyticsEvent = (name: string, route: string) => ({
  listeners: {
    tabPress: () => {
      InteractionManager.runAfterInteractions(() => {
        logRBIEvent({
          name: CustomEventNames.CLICK_EVENT,
          type: EventTypes.Other,
          attributes: {
            text: name,
            component: ClickEventComponentNames.NAV_BAR,
            componentId: route,
          },
        });
      });
    },
  },
});

const ANALYTICS_HOME = createAnalyticsEvent('Home', '/');
const ANALYTICS_MENU = createAnalyticsEvent('Menu', '/menu');
const ANALYTICS_MY_CODE = createAnalyticsEvent('My Code', '/my-code');
const ANALYTICS_OFFERS = createAnalyticsEvent('Offers', '/offers');
const ANALYTICS_REWARDS = createAnalyticsEvent('Rewards', '/rewards');

// this doesn't work on web
const NativeTouchFeedbackTabBarIcon = !isWeb
  ? ({ key, ...props }: any) => <TouchableOpacity key={key} activeOpacity={0.3} {...props} />
  : undefined;

const TAB_ICONS = {
  home: {
    base: require('../../assets/images/nav/home.svg'),
    active: require('../../assets/images/nav/home-active.svg'),
  },
  menu: {
    base: require('../../assets/images/nav/menu.svg'),
    active: require('../../assets/images/nav/menu-active.svg'),
  },
  myCode: {
    base: require('../../assets/images/nav/my-code.svg'),
    active: require('../../assets/images/nav/my-code-active.svg'),
  },
  offers: {
    base: require('../../assets/images/nav/offers.svg'),
    active: require('../../assets/images/nav/offers-active.svg'),
  },
  rewards: {
    base: require('../../assets/images/nav/rewards.svg'),
    active: require('../../assets/images/nav/rewards-active.svg'),
  },
};

type TabBarIconProps = {
  name: string;
  focused: boolean;
  altId: string;
  BadgeComponent?: React.ComponentType | undefined;
};

const TabBarIcon: React.FC<TabBarIconProps> = memo(({ name, focused, altId, BadgeComponent }) => {
  const { formatMessage } = useIntl();
  const alt = formatMessage({ id: altId });
  const appTakeover = useAppTakeover();
  const appTakeoverMenuIcon = name === 'menu' ? appTakeover?.menuIcon : undefined;
  return (
    <View style={tabBarStyles.iconWrapper}>
      <View
        style={[tabBarStyles.activeIndicatorContainer, focused && tabBarStyles.activeIndicator]}
      />
      <Image
        accessibilityLabel={alt}
        source={appTakeoverMenuIcon || (focused ? TAB_ICONS[name].active : TAB_ICONS[name].base)}
        style={tabBarStyles.iconStyles}
        {...hiddenAccessibilityPlatformProps}
      />
      {BadgeComponent ? <BadgeComponent /> : null}
    </View>
  );
});

const TabLabel: React.FC<{ id: string; focused: boolean }> = memo(({ id, focused }) => {
  const { formatMessage } = useIntl();
  const text = formatMessage({ id });

  return (
    <Text style={focused ? tabBarStyles.focusedLabelStyle : tabBarStyles.unfocusedLabelStyle}>
      {text}
    </Text>
  );
});

const MyCodeBadge = memo(() => {
  const { inRestaurantRedemptionCart } = useInRestaurantRedemptionContext();
  const count = inRestaurantRedemptionCart.reduce(
    (acc, currentValue) => acc + currentValue.quantity,
    0
  );

  if (!count) {
    return null;
  }

  return (
    <BadgeOuter>
      <BadgeInner variant="number-solid">{count.toString()}</BadgeInner>
    </BadgeOuter>
  );
});

const BadgeOuter = Box.withConfig({
  position: 'absolute',
  zIndex: 1,
  top: -5,
  right: 5,
});

const BadgeInner = UCLBadge.withConfig({
  bgColor: primitive.bk.$meltyYellow,
  _text: {
    fontFamily: 'heading',
    fontSize: 'xs',
    color: primitive.bk.$bbqBrown,
  },
  width: '$5',
  height: '$5',
  style: {
    minWidth: 'auto',
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0,
    paddingBottom: 0,
  },
});

export const tabBarStyles = StyleSheet.create({
  tabBarStyle: {
    backgroundColor: primitive.bk.$mayoEggWhite,
    borderTopColor: '#FFFFFF',
    borderTopWidth: 1,
  },
  iconWrapper: {
    alignItems: 'center',
  },
  activeIndicatorContainer: {
    height: 3,
    top: -7,
  },
  activeIndicator: {
    backgroundColor: primitive.bk.$bbqBrown,
    width: 70,
  },
  iconStyles: { height: 22, width: 22 },
  focusedLabelStyle: { fontFamily: 'Flame-Regular', color: primitive.bk.$bbqBrown, fontSize: 12 },
  unfocusedLabelStyle: { fontFamily: 'Flame-RegularSans', color: '#82685B', fontSize: 12 },
});
